import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Serializes submissions while allowing a changed conversation scope to move on.
 * @param activeConversationId - Currently selected conversation identifier
 * @param scopeVersion - Version advanced whenever the chat is reset
 * @returns Controls for reserving, restoring, and releasing a submission
 */
export const useConversationSubmissionLock = (
  activeConversationId: string | null,
  scopeVersion: number
) => {
  const activeSubmissionRef = useRef<number | null>(null);
  const cancelledSubmissionRef = useRef<number | null>(null);
  const nextSubmissionIdRef = useRef(0);
  const previousScopeRef = useRef({
    conversationId: activeConversationId,
    version: scopeVersion,
  });

  useLayoutEffect(() => {
    const previousScope = previousScopeRef.current;
    if (
      previousScope.conversationId !== activeConversationId ||
      previousScope.version !== scopeVersion
    ) {
      activeSubmissionRef.current = null;
      previousScopeRef.current = {
        conversationId: activeConversationId,
        version: scopeVersion,
      };
    }
  }, [activeConversationId, scopeVersion]);

  const reserveSubmission = useCallback((): number | null => {
    if (activeSubmissionRef.current !== null) {
      return null;
    }
    nextSubmissionIdRef.current += 1;
    activeSubmissionRef.current = nextSubmissionIdRef.current;
    return activeSubmissionRef.current;
  }, []);

  const restoreSubmission = useCallback((submissionId: number): boolean => {
    if (submissionId === cancelledSubmissionRef.current) {
      return false;
    }
    if (
      activeSubmissionRef.current !== null &&
      activeSubmissionRef.current !== submissionId
    ) {
      return false;
    }
    activeSubmissionRef.current = submissionId;
    return true;
  }, []);

  const releaseSubmission = useCallback((submissionId: number): void => {
    if (activeSubmissionRef.current === submissionId) {
      activeSubmissionRef.current = null;
    }
  }, []);

  // A stopped question still waiting on its conversation must not be sent
  // once the conversation exists, so its submission can't be restored.
  const cancelPendingSubmission = useCallback((): void => {
    cancelledSubmissionRef.current = activeSubmissionRef.current;
  }, []);

  const isSubmissionCancelled = useCallback(
    (submissionId: number): boolean =>
      submissionId === cancelledSubmissionRef.current,
    []
  );

  return {
    cancelPendingSubmission,
    isSubmissionCancelled,
    releaseSubmission,
    reserveSubmission,
    restoreSubmission,
  };
};
